import React, { useState, useEffect } from 'react';
import { reportReviewAPI, vehicleRegistrationAPI } from '../services/api';
import SearchableSelect from './SearchableSelect';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import './ReportReviewManagement.css';

const ReportReviewManagement = ({ user }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('');
  const [handlingFilter, setHandlingFilter] = useState('');
  const [exportingExcel, setExportingExcel] = useState(false);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0 });
  
  // Temp filter states for modal
  const [tempStatusFilter, setTempStatusFilter] = useState('');
  const [tempUnitFilter, setTempUnitFilter] = useState('');
  const [tempVerificationFilter, setTempVerificationFilter] = useState('');
  const [tempHandlingFilter, setTempHandlingFilter] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    licensePlate: '',
    reportContent: '',
    reporterName: '',
    reportCode: '',
    reporterIdCard: '',
    reporterAddress: '',
    reporterPhone: '',
    verificationResult: '',
    handlingResult: ''
  });

  const [uploadLoading, setUploadLoading] = useState(false);
  const [handlingType, setHandlingType] = useState('');
  
  // Upload Excel modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [units, setUnits] = useState([]);
  const [filterUnits, setFilterUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  useEffect(() => {
    fetchReports();
    // Load all units cho admin/supervisor một lần
    if (user.role === 'admin' || user.role === 'supervisor') {
      fetchUnits();
    }
  }, [pagination.page, statusFilter, unitFilter, verificationFilter, handlingFilter, user.role]);

  useEffect(() => {
    if (user.role === 'admin' || user.role === 'supervisor') {
      fetchFilterUnits(tempStatusFilter);
    }
  }, [tempStatusFilter, user.role]);

  useEffect(() => {
    fetchStatistics();
  }, [unitFilter]);

  const fetchStatistics = async () => {
    try {
      const res = await reportReviewAPI.getStatistics({ unitId: unitFilter });
      setStats(res.data);
    } catch (error) {
      console.error('Lỗi khi lấy thống kê:', error);
    }
  };

  const fetchUnits = async () => {
    setUnitsLoading(true);
    try {
      const res = await vehicleRegistrationAPI.getUnits();
      const unitsData = res.data.units || res.data;
      const parsedUnits = Array.isArray(unitsData) ? unitsData : [];
      setUnits(parsedUnits);
      // Khởi tạo filterUnits ban đầu
      setFilterUnits(parsedUnits);
    } catch (error) {
      console.error('Error fetching units:', error);
      Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Không thể tải danh sách đơn vị: ' + (error.response?.data?.message || error.message) });
      setUnits([]);
    } finally {
      setUnitsLoading(false);
    }
  };

  const fetchFilterUnits = async (status) => {
    try {
      const res = await reportReviewAPI.getUnitsWithCompletionStatus({ status });
      const unitsData = res.data;
      setFilterUnits(Array.isArray(unitsData) ? unitsData : []);
    } catch (error) {
      console.error('Error fetching units for filter:', error);
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await reportReviewAPI.getReports({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        status: statusFilter,
        unitId: unitFilter,
        verificationResult: verificationFilter,
        handlingResult: handlingFilter
      });
      setReports(res.data.data);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Error fetching reports:', error);
      Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Có lỗi khi tải danh sách phản ánh' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchReports();
  };

  const exportToExcel = async () => {
    try {
      setExportingExcel(true);
      const res = await reportReviewAPI.getReports({
        page: 1,
        limit: 100000,
        search: searchTerm,
        status: statusFilter,
        unitId: unitFilter,
        verificationResult: verificationFilter,
        handlingResult: handlingFilter
      });
      const allReports = res.data.data || [];
      
      if (allReports.length === 0) {
        Swal.fire({ icon: 'warning', title: 'Không có dữ liệu', text: 'Không có báo cáo nào để xuất!' });
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Báo cáo');

      worksheet.columns = [
        { width: 5 },  
        { width: 15 }, 
        { width: 30 }, 
        { width: 25 }, 
        { width: 20 }, 
        { width: 20 }, 
        { width: 30 }, 
        { width: 15 }, 
        { width: 20 }, 
        { width: 30 }  
      ];

      worksheet.mergeCells('A1:C1');
      worksheet.getCell('A1').value = 'CÔNG AN TỈNH....';
      worksheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getCell('A1').font = { name: 'Times New Roman', size: 12, bold: true };
      
      worksheet.mergeCells('D1:J1');
      worksheet.getCell('D1').value = 'CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM';
      worksheet.getCell('D1').alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getCell('D1').font = { name: 'Times New Roman', size: 12, bold: true };

      worksheet.mergeCells('A2:C2');
      worksheet.getCell('A2').value = 'PHÒNG CẢNH SÁT GIAO THÔNG';
      worksheet.getCell('A2').alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getCell('A2').font = { name: 'Times New Roman', size: 12, bold: true };

      worksheet.mergeCells('D2:J2');
      worksheet.getCell('D2').value = 'Độc lập - Tự do - Hạnh phúc';
      worksheet.getCell('D2').alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getCell('D2').font = { name: 'Times New Roman', size: 12, bold: true, underline: true };

      worksheet.mergeCells('A4:J4');
      worksheet.getCell('A4').value = 'THỐNG KÊ KẾT QUẢ KIỂM TRA, XÁC MINH, XỬ LÝ THÔNG TIN KHAI BÁO LÀM SẠCH DỮ LIỆU';
      worksheet.getCell('A4').alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getCell('A4').font = { name: 'Times New Roman', size: 14, bold: true };
      worksheet.getRow(4).height = 30;

      const headerRow1 = worksheet.getRow(6);
      const headers1 = ['STT', 'BIỂN SỐ', 'NỘI DUNG PHẢN ÁNH', 'NGƯỜI PHẢN ÁNH', 'MÃ PHẢN ÁNH', 'CCCD/MÃ ĐỊNH DANH', 'ĐỊA CHỈ', 'SỐ ĐIỆN THOẠI', 'KẾT QUẢ KIỂM TRA, XÁC MINH', 'KẾT QUẢ XỬ LÝ'];
      headers1.forEach((text, i) => {
        const cell = headerRow1.getCell(i + 1);
        cell.value = text;
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.font = { name: 'Times New Roman', size: 11, bold: true };
        cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
      });
      headerRow1.height = 40;

      let currentRow = 7;
      allReports.forEach((report, idx) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = idx + 1;
        row.getCell(2).value = report.licensePlate || '';
        row.getCell(3).value = report.reportContent || '';
        row.getCell(4).value = report.reporterName || '';
        row.getCell(5).value = report.reportCode || '';
        row.getCell(6).value = report.reporterIdCard || '';
        row.getCell(7).value = report.reporterAddress || '';
        row.getCell(8).value = report.reporterPhone || '';
        row.getCell(9).value = report.verificationResult || '';
        row.getCell(10).value = report.handlingResult || '';

        for (let i = 1; i <= 10; i++) {
          const cell = row.getCell(i);
          cell.font = { name: 'Times New Roman', size: 11 };
          cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
          cell.alignment = { vertical: 'middle', wrapText: true };
          if (i === 1 || i === 2) cell.alignment.horizontal = 'center';
        }
        currentRow++;
      });

      currentRow += 2;
      worksheet.mergeCells(`A${currentRow}:J${currentRow}`);
      const noteCell1 = worksheet.getCell(`A${currentRow}`);
      noteCell1.value = 'GHI CHÚ: Giữ nguyên các thông tin do C08 cung cấp từ cột 1 đến cột 7, cập nhật các thông tin tại cột 8, 9 như sau:';
      noteCell1.font = { name: 'Times New Roman', size: 11, italic: true };
      
      currentRow++;
      worksheet.mergeCells(`A${currentRow}:J${currentRow}`);
      const noteCell2 = worksheet.getCell(`A${currentRow}`);
      noteCell2.value = '- Cột 8 ghi khai báo đúng/ khai báo sai;';
      noteCell2.font = { name: 'Times New Roman', size: 11 };

      currentRow++;
      worksheet.mergeCells(`A${currentRow}:J${currentRow + 1}`);
      const noteCell3 = worksheet.getCell(`A${currentRow}`);
      noteCell3.value = '- Cột 9 ghi nhận đã cập nhật lại số căn cước công dân trên hệ thống/ công dân đã thực hiện thủ tục thu hồi giấy chứng nhận đăng ký, biển số/ đã thực hiện thủ tục đăng ký, sang tên/ đã cung cấp thông tin chủ xe và yêu cầu làm thủ tục nhưng chưa thực hiện đăng ký, sang tên/ đã chuyển cho lực lượng tuần tra kiểm soát, xử lý vi phạm, điều tra giải quyết tai nạn giao thông...';
      noteCell3.font = { name: 'Times New Roman', size: 11 };
      noteCell3.alignment = { wrapText: true, vertical: 'top' };
      worksheet.getRow(currentRow).height = 40;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Bao_Cao_Kiem_Tra_Xac_Minh_${new Date().getTime()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(link.href);

      Swal.fire({ icon: 'success', title: 'Thành công', text: 'Xuất file Excel thành công!', timer: 2000, showConfirmButton: false });
    } catch (error) {
      console.error('Error exporting excel:', error);
      Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Có lỗi khi xuất Excel!' });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openEditModal = (report) => {
    const hr = report.handlingResult || '';
    if ([
      "",
      "Đã cập nhật CCCD trên hệ thống",
      "Công dân đã thực hiện thu hồi đăng ký, biển số xe",
      "Công dân đã thực hiện thủ tục đăng ký, sang tên",
      "Đã cung cấp thông tin chủ xe và yêu cầu làm thủ tục nhưng chưa thực hiện thủ tục đăng ký, sang tên",
      "Đã chuyển cho lực lượng TTKS, XLVP và ĐTGQ TNGT"
    ].includes(hr)) {
      setHandlingType(hr);
    } else {
      setHandlingType('Khác');
    }

    setFormData({
      id: report.id,
      licensePlate: report.licensePlate || '',
      reportContent: report.reportContent || '',
      reporterName: report.reporterName || '',
      reportCode: report.reportCode || '',
      reporterIdCard: report.reporterIdCard || '',
      reporterAddress: report.reporterAddress || '',
      reporterPhone: report.reporterPhone || '',
      verificationResult: report.verificationResult || '',
      handlingResult: hr
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.licensePlate) {
      Swal.fire({ icon: 'warning', title: 'Cảnh báo', text: 'Vui lòng nhập biển số!' });
      return;
    }
    if (!formData.verificationResult || !formData.handlingResult) {
      Swal.fire({ icon: 'warning', title: 'Cảnh báo', text: 'Vui lòng chọn đủ Kết quả xác minh và Kết quả xử lý mới được lưu!' });
      return;
    }

    try {
      if (isEditing) {
        await reportReviewAPI.updateReport(formData.id, formData);
        Swal.fire({ icon: 'success', title: 'Thành công', text: 'Cập nhật thành công!' });
      }
      setShowModal(false);
      fetchReports();
    } catch (error) {
      console.error('Submit error:', error);
      Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Có lỗi xảy ra khi lưu dữ liệu' });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Xác nhận xóa',
      text: 'Bạn có chắc chắn muốn xóa phản ánh này?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Đồng ý xóa',
      cancelButtonText: 'Hủy'
    });

    if (result.isConfirmed) {
      try {
        await reportReviewAPI.deleteReport(id);
        Swal.fire({ icon: 'success', title: 'Thành công', text: 'Đã xóa thành công!' });
        fetchReports();
      } catch (error) {
        console.error('Delete error:', error);
        Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Có lỗi khi xóa' });
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Unit upload trực tiếp
    await uploadExcel(file, null);
    e.target.value = null;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setSelectedFile(file);
    e.target.value = null; // Reset input
  };

  const uploadExcel = async (file, unitId) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    if (unitId) {
      formDataUpload.append('unitId', unitId);
    }

    try {
      setUploadLoading(true);
      const res = await reportReviewAPI.importExcel(formDataUpload);
      
      // Hiển thị kết quả import
      let message = res.data.message;
      
      // Nếu có lỗi, hiển thị chi tiết
      if (res.data.errors && res.data.errors.length > 0) {
        message += '\n\nCác lỗi:\n' + res.data.errors.join('\n');
        if (res.data.totalErrors > res.data.errors.length) {
          message += `\n... và ${res.data.totalErrors - res.data.errors.length} lỗi khác`;
        }
      }
      
      Swal.fire({ icon: 'info', title: 'Kết quả Import', text: message });
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchReports();
      setShowUploadModal(false);
      setSelectedFile(null);
      setSelectedUnitId('');
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.message || 'Có lỗi khi tải lên file Excel';
      
      // Nếu có danh sách lỗi chi tiết từ server
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorList = error.response.data.errors.join('\n');
        Swal.fire({ icon: 'error', title: 'Lỗi Import', text: `${errorMsg}\n\nCác lỗi:\n${errorList}` });
      } else {
        Swal.fire({ icon: 'error', title: 'Lỗi Import', text: errorMsg });
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const handleUploadConfirm = () => {
    if (!selectedUnitId) {
      Swal.fire({ icon: 'warning', title: 'Cảnh báo', text: 'Vui lòng chọn đơn vị!' });
      return;
    }
    if (!selectedFile) {
      Swal.fire({ icon: 'warning', title: 'Cảnh báo', text: 'Vui lòng chọn file!' });
      return;
    }
    uploadExcel(selectedFile, selectedUnitId);
  };

  return (
    <div className="report-review-management">
      <div className="header-row">
        <h2>Rà soát phản ánh</h2>
        <div className="header-actions">
          {(user.role === 'admin' || user.role === 'supervisor') && (
            <div style={{ position: 'relative' }}>
              <button 
                className="btn-secondary" 
                onClick={() => {
                  setShowUploadModal(true);
                }}
                disabled={uploadLoading}
              >
                {uploadLoading ? 'Đang xử lý...' : 'Upload Excel'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="statistics-cards">
        <div className="stat-card total">
          <h4>Tổng số phản ánh</h4>
          <p className="stat-value">{stats.total}</p>
        </div>
        <div className="stat-card completed">
          <h4>Đã hoàn thành</h4>
          <p className="stat-value">{stats.completed}</p>
        </div>
        <div className="stat-card pending">
          <h4>Chưa hoàn thành</h4>
          <p className="stat-value">{stats.pending}</p>
        </div>
      </div>

      <div className="search-filter-section" style={{ flexWrap: 'wrap', gap: '10px' }}>
        <form onSubmit={handleSearch} className="search-form" style={{ marginBottom: 0 }}>
          <input 
            type="text" 
            placeholder="Tìm kiếm biển số, mã phản ánh..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn-primary">Tìm kiếm</button>
        </form>

        <button 
          className="btn-secondary" 
          onClick={() => {
            setTempStatusFilter(statusFilter);
            setTempUnitFilter(unitFilter);
            setTempVerificationFilter(verificationFilter);
            setTempHandlingFilter(handlingFilter);
            setShowFilterModal(true);
          }}
          style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none' }}
        >
          <i className="filter-icon"></i> Bộ lọc
        </button>
        
        <button 
          className="btn-primary" 
          style={{ backgroundColor: '#10b981', border: 'none', marginLeft: 'auto' }}
          onClick={exportToExcel}
          disabled={exportingExcel}
        >
          {exportingExcel ? '⏳ Đang xuất...' : '📊 Xuất Excel'}
        </button>
      </div>

      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th>STT</th>
              <th>Biển số</th>
              <th>Nội dung phản ánh</th>
              <th>Người phản ánh</th>
              <th>Mã phản ánh</th>
              <th>CCCD/Định danh</th>
              <th>Số điện thoại</th>
              <th>KQ xác minh</th>
              <th>KQ xử lý</th>
              <th>Trạng thái</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="11" className="loading">Đang tải...</td></tr>
            ) : reports.length === 0 ? (
              <tr><td colSpan="11" className="no-data">Không có dữ liệu</td></tr>
            ) : (
              reports.map((report, index) => (
                <tr key={report.id}>
                  <td style={{ textAlign: 'center' }}>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                  <td className="license-plate-cell">{report.licensePlate}</td>
                  <td className="text-ellipsis" title={report.reportContent}>{report.reportContent}</td>
                  <td>{report.reporterName}</td>
                  <td>{report.reportCode}</td>
                  <td>{report.reporterIdCard}</td>
                  <td>{report.reporterPhone}</td>
                  <td className="text-ellipsis-sm" title={report.verificationResult}>{report.verificationResult}</td>
                  <td className="text-ellipsis-sm" title={report.handlingResult}>{report.handlingResult}</td>
                  <td>
                    <span className={`status-badge ${report.status === 'Hoàn thành' ? 'completed' : 'pending'}`}>
                      {report.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-cell">
                      <button className="btn-small btn-info" onClick={() => openEditModal(report)}>Sửa</button>
                      {(user.role === 'admin' || user.role === 'supervisor') && (
                        <button className="btn-small btn-danger" onClick={() => handleDelete(report.id)}>Xóa</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Trước
          </button>
          <span className="page-info">Trang {pagination.page} / {pagination.totalPages}</span>
          <button 
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Sau
          </button>
        </div>
      )}

      {/* Modal Filter */}
      {showFilterModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Bộ lọc hiển thị</h3>
              <button onClick={() => setShowFilterModal(false)} className="btn-close">&times;</button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                {(user.role === 'admin' || user.role === 'supervisor') && (
                  <div className="form-group full-width">
                    <label>Đơn vị</label>
                    <SearchableSelect
                      options={[
                        { value: '', label: 'Tất cả đơn vị' },
                        ...filterUnits.map(unit => ({
                          value: unit.id,
                          label: unit.name
                        }))
                      ]}
                      value={tempUnitFilter}
                      onChange={setTempUnitFilter}
                      placeholder="Tất cả đơn vị"
                      emptyText="Không có đơn vị nào"
                    />
                  </div>
                )}

                <div className="form-group full-width">
                  <label>Kết quả xác minh</label>
                  <select 
                    value={tempVerificationFilter} 
                    onChange={(e) => setTempVerificationFilter(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Tất cả KQ xác minh</option>
                    <option value="khai báo đúng">Khai báo đúng</option>
                    <option value="khai báo sai">Khai báo sai</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Kết quả xử lý</label>
                  <select 
                    value={tempHandlingFilter} 
                    onChange={(e) => setTempHandlingFilter(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Tất cả KQ xử lý</option>
                    <option value="Đã cập nhật CCCD trên hệ thống">Đã cập nhật CCCD</option>
                    <option value="Công dân đã thực hiện thu hồi đăng ký, biển số xe">Đã thu hồi đăng ký, biển số</option>
                    <option value="Công dân đã thực hiện thủ tục đăng ký, sang tên">Đã đăng ký, sang tên</option>
                    <option value="Đã cung cấp thông tin chủ xe và yêu cầu làm thủ tục nhưng chưa thực hiện thủ tục đăng ký, sang tên">Chưa thực hiện ĐK, sang tên</option>
                    <option value="Đã chuyển cho lực lượng TTKS, XLVP và ĐTGQ TNGT">Đã chuyển cho TTKS</option>
                    <option value="Khác">Khác</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Trạng thái</label>
                  <select 
                    value={tempStatusFilter} 
                    onChange={(e) => setTempStatusFilter(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Tất cả trạng thái</option>
                    <option value="Chưa hoàn thành">Chưa hoàn thành</option>
                    <option value="Hoàn thành">Hoàn thành</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  setTempUnitFilter('');
                  setTempVerificationFilter('');
                  setTempHandlingFilter('');
                  setTempStatusFilter('');
                }}
              >
                Xóa bộ lọc
              </button>
              <button 
                type="button" 
                className="btn-primary" 
                onClick={() => {
                  setUnitFilter(tempUnitFilter);
                  setVerificationFilter(tempVerificationFilter);
                  setHandlingFilter(tempHandlingFilter);
                  setStatusFilter(tempStatusFilter);
                  setPagination(prev => ({ ...prev, page: 1 }));
                  setShowFilterModal(false);
                }}
              >
                Áp dụng / Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Edit/Add */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Cập nhật phản ánh / Nhập kết quả</h3>
              <button onClick={() => setShowModal(false)} className="btn-close">&times;</button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  {/* 1. Biển số */}
                  <div className="form-group full-width">
                    <label>Biển số (*)</label>
                    <input type="text" name="licensePlate" value={formData.licensePlate} onChange={handleInputChange} required />
                  </div>
                  
                  {/* 2. Nội dung phản ánh */}
                  <div className="form-group full-width">
                    <label>Nội dung phản ánh</label>
                    <textarea name="reportContent" value={formData.reportContent} onChange={handleInputChange} rows="3"></textarea>
                  </div>

                  {/* 3. Người phản ánh */}
                  <div className="form-group">
                    <label>Người phản ánh</label>
                    <input type="text" name="reporterName" value={formData.reporterName} onChange={handleInputChange} />
                  </div>
                  
                  {/* 4. Mã phản ánh */}
                  <div className="form-group">
                    <label>Mã phản ánh</label>
                    <input type="text" name="reportCode" value={formData.reportCode} onChange={handleInputChange} />
                  </div>
                  
                  {/* 5. CCCD / Mã định danh */}
                  <div className="form-group">
                    <label>CCCD / Mã định danh</label>
                    <input type="text" name="reporterIdCard" value={formData.reporterIdCard} onChange={handleInputChange} />
                  </div>
                  
                  {/* 6. Địa chỉ */}
                  <div className="form-group">
                    <label>Địa chỉ</label>
                    <input type="text" name="reporterAddress" value={formData.reporterAddress} onChange={handleInputChange} />
                  </div>
                  
                  {/* 7. Số điện thoại - full width */}
                  <div className="form-group full-width">
                    <label>Số điện thoại</label>
                    <input type="text" name="reporterPhone" value={formData.reporterPhone} onChange={handleInputChange} />
                  </div>

                  {/* 8. Kết quả kiểm tra xác minh */}
                  <div className="form-group full-width">
                    <label>Kết quả kiểm tra xác minh (*)</label>
                    <select name="verificationResult" value={formData.verificationResult} onChange={handleInputChange} required>
                      <option value="">-- Chọn kết quả xác minh --</option>
                      <option value="khai báo đúng">khai báo đúng</option>
                      <option value="khai báo sai">khai báo sai</option>
                    </select>
                  </div>
                  
                  {/* 9. Kết quả xử lý */}
                  <div className="form-group full-width">
                    <label>Kết quả xử lý (*)</label>
                    <select 
                      value={handlingType} 
                      onChange={(e) => {
                        const val = e.target.value;
                        setHandlingType(val);
                        if (val !== 'Khác') {
                          setFormData(prev => ({ ...prev, handlingResult: val }));
                        } else {
                          setFormData(prev => ({ ...prev, handlingResult: '' }));
                        }
                      }}
                      style={{ marginBottom: handlingType === 'Khác' ? '10px' : '0', width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                      required
                    >
                      <option value="">-- Chọn kết quả xử lý --</option>
                      <option value="Đã cập nhật CCCD trên hệ thống">Đã cập nhật CCCD trên hệ thống</option>
                      <option value="Công dân đã thực hiện thu hồi đăng ký, biển số xe">Công dân đã thực hiện thu hồi đăng ký, biển số xe</option>
                      <option value="Công dân đã thực hiện thủ tục đăng ký, sang tên">Công dân đã thực hiện thủ tục đăng ký, sang tên</option>
                      <option value="Đã cung cấp thông tin chủ xe và yêu cầu làm thủ tục nhưng chưa thực hiện thủ tục đăng ký, sang tên">Đã cung cấp thông tin chủ xe và yêu cầu làm thủ tục nhưng chưa thực hiện thủ tục đăng ký, sang tên</option>
                      <option value="Đã chuyển cho lực lượng TTKS, XLVP và ĐTGQ TNGT">Đã chuyển cho lực lượng TTKS, XLVP và ĐTGQ TNGT</option>
                      <option value="Khác">Khác (nhập nội dung)</option>
                    </select>
                    
                    {handlingType === 'Khác' && (
                      <textarea 
                        name="handlingResult" 
                        value={formData.handlingResult} 
                        onChange={handleInputChange} 
                        rows="2"
                        placeholder="Nhập nội dung xử lý khác..."
                        required
                      ></textarea>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => setShowModal(false)} className="btn-cancel">Hủy</button>
                  <button type="submit" className="btn-submit">Lưu lại</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Upload Excel Modal - Chọn đơn vị trước, chọn file sau */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Upload Excel - Chọn đơn vị</h3>
              <button onClick={() => {
                setShowUploadModal(false);
                setSelectedFile(null);
                setSelectedUnitId('');
              }} className="btn-close">&times;</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Chọn đơn vị (*)</label>
                {unitsLoading ? (
                  <div style={{ padding: '10px', color: '#666', fontSize: '14px' }}>
                    Đang tải danh sách đơn vị...
                  </div>
                ) : (
                  <SearchableSelect
                    options={units.map(unit => ({
                      value: unit.id,
                      label: unit.name
                    }))}
                    value={selectedUnitId}
                    onChange={setSelectedUnitId}
                    placeholder="-- Chọn đơn vị --"
                    emptyText="Không có đơn vị nào"
                  />
                )}
                {!unitsLoading && Array.isArray(units) && units.length === 0 && (
                  <p style={{ color: '#f44336', fontSize: '13px', marginTop: '5px' }}>
                    ⚠ Chưa có đơn vị nào trong hệ thống
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>Chọn file Excel (*)</label>
                <input 
                  type="file" 
                  accept=".xlsx, .xls"
                  onChange={handleFileSelect}
                  disabled={!selectedUnitId}
                  style={{ 
                    width: '100%', 
                    padding: '10px', 
                    borderRadius: '4px', 
                    border: '1px solid #ddd', 
                    fontSize: '14px',
                    cursor: !selectedUnitId ? 'not-allowed' : 'pointer',
                    opacity: !selectedUnitId ? 0.6 : 1
                  }}
                />
                {!selectedUnitId && (
                  <p style={{ color: '#ff9800', fontSize: '13px', marginTop: '5px' }}>
                    ⚠ Vui lòng chọn đơn vị trước
                  </p>
                )}
                {selectedFile && (
                  <p style={{ color: '#4caf50', fontSize: '14px', marginTop: '5px' }}>
                    ✓ {selectedFile.name}
                  </p>
                )}
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setSelectedUnitId('');
                  }} 
                  className="btn-cancel"
                >
                  Hủy
                </button>
                <button 
                  type="button"
                  onClick={handleUploadConfirm}
                  className="btn-submit"
                  disabled={uploadLoading}
                >
                  {uploadLoading ? 'Đang import...' : 'Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ReportReviewManagement;
